import {
  EndOfNextPeriod,
  GetGranularityConfiguration,
  Granularity,
} from "@hydrologiq/common/types/reporting/Granularity"
import { useLazyQuery, useQuery } from "@apollo/client"
import * as gqlTypes from "@hydrologiq/gql/graphql"
import { IANATimeZone, toTimestamp, startOfDay, endOfDay } from "@hydrologiq/common/helpers/date"
import Reporting, { ReportingSkeleton } from "@hydrologiq/shared/reporting/Reporting"
import { DateTime, days } from "timezonecomplete"
import InstallationUsageDownload from "./InstallationUsageDownload"
import { useState, useMemo, useEffect } from "react"
import ReportingChart from "@hydrologiq/shared/reporting/ReportingChart"
import { ReportingChartSkeleton } from "@hydrologiq/shared/reporting/ReportingChart"
import { FieldSelector } from "@hydrologiq/shared/reporting/FieldSelector"
import { getAvailableFields } from "@hydrologiq/shared/reporting/usageCsv"
import { type RowType } from "@hydrologiq/shared/reporting/rows"
export type SystemUsageView = "chart" | "table"

interface InstallationUsageProps {
  start: DateTime
  end: DateTime
  granularity: Granularity
  installationId: string
  installationName: string
  view: SystemUsageView
  latest?: boolean
  title?: boolean
  availableFields?: (keyof RowType)[]
}

export function InstallationUsageSkeleton({ view, title }: { view: SystemUsageView; title?: boolean }) {
  return view === "table" ? <ReportingSkeleton /> : <ReportingChartSkeleton title={title} />
}

function InstallationUsage({
  start,
  end,
  installationId,
  installationName,
  granularity,
  view,
  latest,
  title,
  availableFields: restrictedFields,
}: InstallationUsageProps) {
  const [downloading, setDownloading] = useState<boolean>(false)
  const granularityConfig = GetGranularityConfiguration(granularity)

  let endOfPreview = EndOfNextPeriod(start, granularity, 10)
  if (endOfPreview.greaterThan(end)) endOfPreview = end

  const [getUsage, { loading, data }] = useLazyQuery(gqlTypes.GetInstallationUsageDocument)

  useEffect(() => {
    getUsage({
      variables: {
        id: installationId,
        start: toTimestamp(latest ? startOfDay(end.sub(days(9))) : start, true),
        end: toTimestamp(latest ? endOfDay(end) : endOfPreview, true),
        freq: granularityConfig.queryFrequency,
        tz: IANATimeZone(),
      },
    })
  }, [start, end, granularity, installationId, latest, restrictedFields])

  // Get available fields from data, filtered by restricted fields if provided
  const availableFields = useMemo(() => {
    const rows = data?.usage || []
    const dataFields = getAvailableFields(rows)

    // If restricted fields are provided, filter the available fields
    if (restrictedFields && restrictedFields.length > 0) {
      return dataFields.filter((field) => restrictedFields.includes(field))
    }

    return dataFields
  }, [data, restrictedFields])

  // Initialise selected fields with all available fields
  const [selectedFields, setSelectedFields] = useState<(keyof RowType)[]>([])

  // Update selected fields when available fields change (first load)
  useMemo(() => {
    if (availableFields.length > 0 && selectedFields.length === 0) {
      setSelectedFields(availableFields)
    }
  }, [availableFields, selectedFields.length])

  let chartFields = []
  if (availableFields.includes("usedWeight")) chartFields.push("usedWeight")
  if (availableFields.includes("usedEnergy")) chartFields.push("usedEnergy")

  return view === "table" ? (
    <Reporting
      start={start}
      end={end}
      granularity={granularity}
      onDataCallback={() => (data?.usage ? data.usage : [])}
      onDownload={() => setDownloading(true)}
      downloading={downloading}
      loading={loading}
      visibleFields={selectedFields}
      fieldSelector={
        availableFields.length > 0 && (
          <FieldSelector
            availableFields={availableFields}
            selectedFields={selectedFields}
            onFieldsChange={setSelectedFields}
            defaultFields={availableFields}
          />
        )
      }
    >
      <InstallationUsageDownload
        start={start}
        end={end}
        granularity={granularity}
        installation={installationId}
        installationName={installationName}
        callback={() => setDownloading(false)}
        selectedFields={selectedFields}
      />
    </Reporting>
  ) : (
    <ReportingChart
      onDataCallback={() => (data?.usage ? data.usage : [])}
      title={title}
      loading={loading}
      fields={chartFields}
    />
  )
}

export default InstallationUsage
